import React, { useEffect, useState } from 'react';
import { Button, Card, Space, Tag, Typography } from 'antd';
import { formatAgo } from './formatAgo';
import { formatGameTime } from '../gameTime/gameTimeReader';
import { buffEndGameSec, buffRemainingSec, formatRemaining } from '../gameEvents/wickelineTracker';

// 1초마다 지금의 실제 시각을 갱신한다(로그가 있을 때만). "N분 M초 전"을 계속 최신으로 보여 주려는 용도다.
function useNow(active) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) {
      return undefined;
    }
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

/**
 * 게임 로그(웹 전용, PiP에는 표시하지 않는다). 상단 우측 패널의 남은 팀 수/인원이 바뀐 것을 최신순으로 보여준다.
 * 한 줄 형식: 게임시간[N분 M초 전] / 몇일차 낮·밤 / 로그. 게임시간은 기록할 때 좌측 하단 시계에서 읽어 저장한 값이고,
 * "몇 분 몇 초 전"은 기록한 실제 시각과 지금의 실제 시각의 차이다(시계를 다시 읽지 않는다).
 * - active: 지금 게임 중이고 화면을 읽고 있는지(안내 문구용)
 */
// 위클라인 처치 여부와 위클 버프(3분 30초) 남은 시간. 처치를 감지한 실제 시각과 지금의 실제 시각으로 센다.
function WickelineStatus({ kill, active, now }) {
  if (!kill) {
    return active ? <Typography.Text type="secondary" style={{ fontSize: 13 }}>위클라인: 아직 처치 안 됨</Typography.Text> : null;
  }
  const remaining = buffRemainingSec(kill.at.getTime(), now);
  const end = formatGameTime(buffEndGameSec(kill.gameSec));
  return (
    <Space size={6} wrap style={{ fontSize: 13 }}>
      <Tag color="volcano" style={{ margin: 0 }}>위클라인 처치됨</Tag>
      {remaining > 0 ? (
        <Typography.Text>
          위클 버프 <b>{formatRemaining(remaining)}</b> 남음
          {end ? <Typography.Text type="secondary"> (게임시간 {end}에 종료)</Typography.Text> : null}
        </Typography.Text>
      ) : (
        <Typography.Text type="secondary">위클 버프 종료됨</Typography.Text>
      )}
    </Space>
  );
}

export default function GameLogCard({ entries, summary, wickelineKill = null, wickelineDiag = null, onSaveKillFrames = null, active, fill = false }) {
  const newestFirst = [...entries].reverse();
  const now = useNow(entries.length > 0 || wickelineKill !== null);
  const hasSummary = summary.teams !== null || summary.players !== null || summary.playersHidden;

  return (
    <Card
      size="small"
      title="게임 로그"
      style={fill ? { minHeight: 'calc(100vh - 150px)' } : undefined}
      extra={hasSummary && (
        <Space size={4}>
          {summary.teams !== null && <Tag color="blue">남은 팀 {summary.teams}</Tag>}
          <Tag color={summary.playersHidden ? 'default' : 'green'}>
            인원 {summary.playersHidden ? '?' : (summary.players === null ? '-' : summary.players)}
          </Tag>
        </Space>
      )}
    >
      {(wickelineKill || active) && (
        <div style={{ marginBottom: 8 }}>
          <WickelineStatus kill={wickelineKill} active={active} now={now} />
        </div>
      )}
      {newestFirst.length === 0 ? (
        <Typography.Text type="secondary">
          {active
            ? '화면 오른쪽 위의 "N TEAMS / 인원" 패널을 읽는 중이에요. 값이 바뀌면 여기에 기록돼요.'
            : '[게임 시작] 후 남은 팀 수와 인원이 바뀔 때마다 여기에 기록돼요.'}
        </Typography.Text>
      ) : (
        <div style={{ maxHeight: fill ? 'calc(100vh - 290px)' : 260, minHeight: fill ? 200 : undefined, overflowY: 'auto' }}>
          {newestFirst.map((entry) => (
            <div key={entry.id} style={{ display: 'flex', flexWrap: 'wrap', gap: '0 8px', padding: '3px 0', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>
              <span style={{ flexShrink: 0, minWidth: 150 }}>
                <b>{entry.gameTime || '--:--'}</b>
                <Typography.Text type="secondary"> [{formatAgo(now - entry.at.getTime())}]</Typography.Text>
              </span>
              <Typography.Text type="secondary" style={{ flexShrink: 0, minWidth: 62 }}>{entry.phaseLabel || ''}</Typography.Text>
              <span>{entry.text}</span>
            </div>
          ))}
        </div>
      )}
      {wickelineDiag && (active || wickelineDiag.killAlerts > 0) && (
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 12, color: '#8c8c8c' }}>
          <span>
            진단 · 처치 알림 {wickelineDiag.killAlerts}회 · 위클라인 {wickelineDiag.wickelineKills}회
            {wickelineDiag.lastScore !== null ? ` · 위클라인 사진 점수 ${wickelineDiag.lastScore.toFixed(2)}(최고 ${wickelineDiag.maxScore.toFixed(2)})` : ''}
          </span>
          {onSaveKillFrames && (
            <Button size="small" onClick={onSaveKillFrames} disabled={wickelineDiag.saved === 0}>
              처치 알림 프레임 저장 ({wickelineDiag.saved}장)
            </Button>
          )}
        </div>
      )}
      <Typography.Paragraph type="secondary" style={{ margin: '8px 0 0', fontSize: 12 }}>
        후반에는 게임이 인원을 ?로 가려서 인원은 기록되지 않아요(남은 팀 수는 계속 기록돼요). 같은 값이 연속 두 번 읽혀야 기록해요.
      </Typography.Paragraph>
    </Card>
  );
}
